/*
 * Sign up / log in modal + shared auth-card behaviour.
 * Drives both the storefront <auth-modal> and the standalone /account pages.
 */
(function () {
  'use strict';

  function setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  function getCookie(name) {
    const nameEQ = `${name}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
      let c = parts[i];
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  function eraseCookie(name) {
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }

  const returnKey = 'ne_auth_return';
  const authPath = /\/account\/(login|register|recover|reset|activate)(\/|$)/;

  // Accept storefront URLs only. Never send a customer to a supplied external
  // host or back into authentication after a successful submission.
  function safeReturnTo(value) {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      const decodedPath = decodeURIComponent(url.pathname);
      if (url.origin !== window.location.origin || decodedPath.startsWith('//') ||
          decodedPath.includes('\\') || authPath.test(decodedPath) ||
          decodedPath.includes('/customer_authentication/') ||
          /[\u0000-\u001f\u007f]/.test(value)) return null;
      url.searchParams.delete('auth');
      url.searchParams.delete('return_to');
      return url.pathname + url.search + url.hash;
    } catch {
      return null;
    }
  }

  function savedReturnTo() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(returnKey));
      if (saved && Date.now() - saved.at < 30 * 60 * 1000) return safeReturnTo(saved.url);
    } catch {
      // Storage can be disabled. The form's return_to still works without it.
    }
    return null;
  }

  function forgetReturnTo() {
    try {
      sessionStorage.removeItem(returnKey);
    } catch { /* Storage is optional. */ }
  }

  function returnDestination(explicit, isAccountPage) {
    const current = isAccountPage ? null : safeReturnTo(window.location.href);
    const params = new URLSearchParams(window.location.search);
    const product = getCookie('loyalty_exclusive_product');
    const collection = getCookie('nec_exclusive_collection');
    const gated = product ? `/products/${encodeURIComponent(product)}`
      : collection ? `/collections/${encodeURIComponent(collection)}` : null;
    const requested = safeReturnTo(explicit) || safeReturnTo(params.get('return_to'));
    // Liquid links can only carry request.path. Keep filters and anchors when
    // that path is the page already underneath the modal.
    if (current && requested === window.location.pathname) return current;
    return requested ||
      // Exclusive-content cookies apply only when entering from rewards.
      (/\/pages\/rewards\/?$/.test(window.location.pathname) && safeReturnTo(gated)) ||
      current || savedReturnTo() || safeReturnTo(document.referrer) || '/';
  }

  function prepareReturnTo(modal, explicit) {
    if (!modal.panel) return;
    modal.returnTo = returnDestination(explicit, !!modal.pageCloseUrl);
    const destination = new URL(modal.returnTo, window.location.origin);
    // Preserve the existing rewards opt-in step, now over the original page.
    destination.searchParams.set('auth', 'rewards');
    modal.querySelectorAll('input[name="return_to"]').forEach((input) => {
      input.value = destination.pathname + destination.search + destination.hash;
    });
    modal.querySelectorAll('a[data-auth-close]').forEach((link) => {
      link.href = modal.returnTo;
    });
  }

  function rememberReturnTo(modal) {
    try {
      sessionStorage.setItem(returnKey, JSON.stringify({ url: modal.returnTo, at: Date.now() }));
    } catch { /* Native return_to does not depend on storage. */ }
    setCookie('loyalty_return_to', modal.returnTo.replace(/;/g, '%3B'), 1);
    // The destination now includes any active exclusive-content flow. Do not
    // let an older cookie override it when the rewards modal finishes.
    eraseCookie('loyalty_exclusive_product');
    eraseCookie('nec_exclusive_collection');
    if (modal.returnTo.includes('true-fans') || modal.returnTo.includes('/team/')) {
      setCookie('came_from_true_fans', '1', 1);
    }
  }

  /* ---- Auth card: view switching, show-password, age gate ---- */

  function setActiveView(card, view, focus) {
    const views = card.querySelectorAll('[data-auth-view]');
    let matched = null;
    views.forEach((el) => {
      const isMatch = el.dataset.authView === view;
      el.classList.toggle('is-active', isMatch);
      if (isMatch) matched = el;
    });
    if (matched && focus) {
      const title = matched.querySelector('.ne-auth__title');
      const focusable = title || matched.querySelector('input:not([type="hidden"]), button, a');
      if (focusable) focusable.focus({ preventScroll: true });
    }
    return matched;
  }

  function initCard(card) {
    if (card.dataset.authReady) return;
    card.dataset.authReady = 'true';

    if (card.querySelector('[data-auth-view]')) {
      // Prefer a view carrying a *visible* server-rendered error/success message
      // so a reload after a failed submit lands on the right panel. Ignore the
      // always-present hidden placeholders (e.g. the empty message box).
      let flagged = null;
      card
        .querySelectorAll(
          '[data-auth-view] .ne-auth__message--error, [data-auth-view] .ne-auth__message--success'
        )
        .forEach((el) => {
          if (!flagged && el.textContent.trim() && getComputedStyle(el).display !== 'none') {
            flagged = el;
          }
        });
      const startView = flagged
        ? flagged.closest('[data-auth-view]').dataset.authView
        : window.location.hash === '#recover' ? 'recover' : card.dataset.authDefault || 'login';
      setActiveView(card, startView, false);
    }

    card.addEventListener('click', (event) => {
      const goto = event.target.closest('[data-auth-goto]');
      if (goto && card.contains(goto)) {
        event.preventDefault();
        setActiveView(card, goto.dataset.authGoto, true);
      }
    });

    // Show / hide password
    card.querySelectorAll('[data-auth-show-password]').forEach((toggle) => {
      toggle.addEventListener('change', () => {
        const form = toggle.closest('form');
        const pw = form && form.querySelector('input[name="customer[password]"]');
        if (pw) pw.type = toggle.checked ? 'text' : 'password';
      });
    });

    // novalidate lets Shopify render field errors, but also skips required on
    // the age checkbox. Block that submission before it reaches Shopify.
    card.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        const age = form.querySelector('[name="over18"]');
        if (!age || age.checked) return;
        event.preventDefault();
        event.stopPropagation();
        const box = form.querySelector('[data-auth-message]');
        if (box) {
          box.className = 'ne-auth__message ne-auth__message--error';
          box.textContent = window.themeStrings?.confirmOver18 ?? 'Please confirm that you are over 18 to create an account.';
          box.hidden = false;
          box.setAttribute('tabindex', '-1');
          box.focus({ preventScroll: true });
        }
        age.setAttribute('aria-invalid', 'true');
        age.focus({ preventScroll: true });
      },
      true
    );
  }

  // Keep Shopify's CAPTCHA submission handlers. Intercept only their completed
  // form, including an asynchronous call to form.submit() after verification.
  function bindBackgroundForms(modal) {
    if (modal.id !== 'AuthModal' || modal.pageCloseUrl || !window.NECustomerSession) return;
    const submissions = new WeakMap();
    modal.querySelectorAll('[data-auth-view="login"] form, [data-auth-view="register"] form').forEach((form) => {
      const nativeSubmit = form.submit.bind(form);
      const hasCaptcha = () => !!form.querySelector('[name="h-captcha-response"]')?.value.trim();
      const message = form.querySelector('[data-auth-message]');
      if (!message) return;

      function report(text) {
        message.className = 'ne-auth__message ne-auth__message--error';
        message.textContent = text;
        message.hidden = false;
        message.setAttribute('role', 'alert');
        message.setAttribute('tabindex', '-1');
        if (modal.isOpen) message.focus({ preventScroll: true });
      }

      const submit = async () => {
        if (modal.submitting) return;
        const age = form.querySelector('[name="over18"]');
        if (age && !age.checked) return;
        if (!hasCaptcha()) return nativeSubmit();
        const action = new URL(form.action, window.location.origin);
        if (action.origin !== window.location.origin) return;
        rememberReturnTo(modal);
        modal.submitting = true;
        form.setAttribute('aria-busy', 'true');
        const buttons = Array.from(modal.querySelectorAll('button[type="submit"], [data-auth-goto]'));
        const disabled = buttons.map((button) => button.disabled);
        buttons.forEach((button) => { button.disabled = true; });
        message.className = 'ne-auth__message';
        message.textContent = modal.dataset.loadingMessage;
        message.hidden = false;
        message.setAttribute('role', 'status');
        let uncertain = false;
        try {
          const response = await fetch(action, {
            method: 'POST', body: new FormData(form), credentials: 'same-origin',
            cache: 'no-store', signal: AbortSignal.timeout(30000),
          });
          const destination = new URL(response.url);
          if (destination.origin !== window.location.origin) throw new Error('External authentication');
          if (/\/challenge(?:\/|$)/.test(destination.pathname)) {
            window.location.assign(destination.href);
            return;
          }
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const view = form.closest('[data-auth-view]').dataset.authView;
          const error = html.querySelector(`[data-auth-view="${view}"] .ne-auth__message--error`);
          if (error?.textContent.trim()) {
            report(error.textContent.trim());
            return;
          }
          await window.NECustomerSession.refresh();
          forgetReturnTo();
          const wasOpen = modal.isOpen;
          if (wasOpen) modal.close();
          modal.remove();
          if (wasOpen) modalFor('rewards')?.open(null, modal.opener);
        } catch {
          // A failed fetch may already have authenticated the customer. Never
          // replay the credential POST; offer a GET to reconcile the session.
          uncertain = true;
          report(modal.dataset.errorMessage);
          const link = document.createElement('a');
          link.href = modal.returnTo;
          link.textContent = modal.dataset.continueMessage;
          message.append(document.createElement('br'), link);
        } finally {
          modal.submitting = false;
          form.removeAttribute('aria-busy');
          buttons.forEach((button, index) => {
            button.disabled = uncertain || disabled[index];
          });
        }
      };
      form.submit = submit;
      submissions.set(form, { submit, hasCaptcha });
    });
    modal.onBackgroundSubmit = (event) => {
      const submission = submissions.get(event.target);
      if (!submission || event.defaultPrevented) return;
      if (modal.submitting) { event.preventDefault(); return; }
      if (!submission.hasCaptcha()) return;
      event.preventDefault();
      void submission.submit();
    };
    window.addEventListener('submit', modal.onBackgroundSubmit);
  }

  /* ---- Modal element ---- */

  class AuthModal extends HTMLElement {
    constructor() {
      super();
      this.panel = this.querySelector('.ne-auth');
      this.dialog = this.querySelector('[role="dialog"]');
      this.onKeydown = this.onKeydown.bind(this);
    }

    // On the account pages the modal is always open, so there is nothing to
    // hide: closing leaves for this URL instead.
    get pageCloseUrl() {
      return this.dataset.authPage || null;
    }

    connectedCallback() {
      if (this.panel) initCard(this.panel);
      this.querySelectorAll('[data-auth-close]').forEach((el) =>
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        })
      );
      prepareReturnTo(this);
      bindBackgroundForms(this);
      if (this.pageCloseUrl) this.open();
      // Remember the origin before Shopify's CAPTCHA handler resumes submission.
      this.addEventListener('submit', (event) => {
        const age = event.target.querySelector('[name="over18"]');
        if (!this.panel || (age && !age.checked)) return;
        rememberReturnTo(this);
      }, true);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.onBackgroundSubmit) window.removeEventListener('submit', this.onBackgroundSubmit);
    }

    open(view, opener, returnTo) {
      prepareReturnTo(this, returnTo);
      if (!this.isOpen) {
        this.opener = opener || document.activeElement;
        this.wasScrollLocked = document.body.classList.contains('overflow-hidden');
      }
      this.isOpen = true;
      if (view && this.panel) setActiveView(this.panel, view, false);
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      // The theme's generic trap includes hidden fields from the other views.
      // This modal computes visible controls on each Tab instead.
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.dialog?.focus({ preventScroll: true });
    }

    close() {
      if (this.pageCloseUrl) {
        forgetReturnTo();
        window.location.href = this.returnTo || this.pageCloseUrl;
        return;
      }
      this.isOpen = false;
      this.hidden = true;
      if (!this.wasScrollLocked) document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      else if (this.opener && this.opener.focus) this.opener.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key !== 'Tab' || !this.dialog) return;
      const controls = Array.from(this.dialog.querySelectorAll(
        'a[href], button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'
      )).filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      if (!first || !controls.includes(active) ||
          (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      }
    }
  }
  customElements.define('auth-modal', AuthModal);

  /* ---- Wiring ---- */

  // 'rewards' lives in its own modal because it is the one view that requires a
  // signed-in customer; the rest share the logged-out auth card.
  function modalFor(view) {
    const el = document.getElementById(view === 'rewards' ? 'LoyaltyModal' : 'AuthModal');
    return el && typeof el.open === 'function' ? el : null;
  }

  function initTriggers() {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
          event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('[data-auth-open], a[href]');
      if (!trigger || trigger.hasAttribute('download') ||
          (trigger.target && trigger.target !== '_self')) return;
      const url = trigger.href ? new URL(trigger.href, window.location.href) : null;
      if (url && url.origin !== window.location.origin) return;
      if (trigger.dataset.exclusiveCollection) {
        setCookie('nec_exclusive_collection', trigger.dataset.exclusiveCollection, 365);
      }
      // Header v3 uses /account in both Liquid and React. For logged-out
      // visitors that is a login entry point too; signed-in pages have no AuthModal.
      const accountPath = url?.pathname.match(/\/account(?:\/(login|register))?\/?$/);
      const pathView = accountPath ? accountPath[1] || 'login' : null;
      const view = trigger.dataset.authOpen || pathView;
      if (!view) return;
      const modal = modalFor(view) || (document.body.classList.contains('is-logged') ? modalFor('rewards') : modalFor('login'));
      if (!modal) return;
      event.preventDefault();
      modal.open(view === 'rewards' ? null : view, trigger, url?.searchParams.get('return_to'));
    });
  }

  function initParamOpen() {
    const url = new URL(window.location.href);
    const view = url.searchParams.get('auth');
    if (!['login', 'register', 'rewards'].includes(view)) return;
    const modal = modalFor(view);
    if (modal) modal.open(view === 'rewards' ? null : view, null);
    url.searchParams.delete('auth');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  }

  // Shopify normally honors the form field. Retain a per-tab fallback for
  // registration responses that land on /account, but only after Liquid has
  // confirmed a signed-in customer. Never infer authentication from a response URL.
  function finishAuthentication() {
    if (!document.body.classList.contains('is-logged')) return false;
    const destination = savedReturnTo();
    forgetReturnTo();
    if (destination && /\/account\/?$/.test(window.location.pathname) &&
        destination !== safeReturnTo(window.location.href)) {
      const url = new URL(destination, window.location.origin);
      url.searchParams.set('auth', 'rewards');
      window.location.replace(url.pathname + url.search + url.hash);
      return true;
    }
    return false;
  }

  function initSignupTracking() {
    if (new URLSearchParams(window.location.search).has('redirect') &&
        window.TrueFansTracker && getCookie('came_from_true_fans')) {
      window.TrueFansTracker.trackAccountCreated('login');
      eraseCookie('came_from_true_fans');
    }
    const register = document.getElementById('create_customer');
    if (!register) return;
    register.addEventListener('submit', (event) => {
      const q = (sel) => event.target.querySelector(sel)?.value;
      if (window.Shopify && window.Shopify.analytics) {
        window.Shopify.analytics.publish('signup', {
          first_name: q('#RegisterForm-FirstName'),
          last_name: q('#RegisterForm-LastName'),
          email: q('#RegisterForm-email'),
        });
      }
      if (window.TrueFansTracker && getCookie('came_from_true_fans')) {
        window.TrueFansTracker.trackAccountCreated('email');
        eraseCookie('came_from_true_fans');
      }
    });
  }

  function init() {
    document.querySelectorAll('.ne-auth').forEach(initCard);
    if (finishAuthentication()) return;
    initTriggers();
    initParamOpen();
    initSignupTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
